import { Home, PieChart, TrendingUp, BarChart3 } from "lucide-react";
import { useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { cn } from "~/lib/utils";
import { useLanguage } from "~/contexts/LanguageContext";

interface NavItem {
  id: string;
  labelKey: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

const navItems: NavItem[] = [
  {
    id: "dashboard",
    labelKey: "navigation.dashboard",
    href: "/",
    icon: Home,
  },
  {
    id: "portfolio",
    labelKey: "navigation.portfolio", 
    href: "/portfolio",
    icon: PieChart,
  },
  {
    id: "insights",
    labelKey: "navigation.insights",
    href: "/insights",
    icon: BarChart3,
  },
  {
    id: "strategies",
    labelKey: "navigation.strategies",
    href: "/strategies", 
    icon: TrendingUp,
  },
];

export function MobileNavigation() {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  // Check if we're in standalone mode (PWA)
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;

  return (
    <nav 
      className="fixed left-0 right-0 z-50 border-t bg-card/98 backdrop-blur supports-[backdrop-filter]:bg-card/95 shadow-[0_-2px_10px_rgba(0,0,0,0.1)] pb-2" 
      style={{ bottom: 0 }}
      data-bottom-navigation
    >
      <div 
        className="grid h-16 grid-cols-4 px-2 py-3" 
      >
        {navItems.map((item) => {
          const Icon = item.icon;
          // Special handling for routes with sub-routes
          const isActive = (item.id === 'portfolio' || item.id === 'strategies' || item.id === 'insights') 
            ? location.pathname.startsWith(item.href)
            : location.pathname === item.href;
          
          return (
            <button
              key={item.id}
              onClick={() => navigate(item.href)}
              className={cn(
                "flex flex-col items-center justify-center space-y-1 text-xs relative group",
                "transition-all duration-300 ease-out",
                isActive
                  ? "text-primary scale-110"
                  : "text-muted-foreground hover:text-foreground hover:scale-105"
              )}
            >
              
              {/* Icon with subtle scale animation */}
              <div className={cn(
                "relative z-10 transition-all duration-300 ease-out",
                isActive ? "scale-115" : "group-hover:scale-110"
              )}>
                <Icon className={cn(
                  "h-5 w-5 transition-colors duration-300 ease-out",
                  isActive ? "text-primary" : "group-hover:text-foreground"
                )} />
              </div>
              
              {/* Label with subtle transition */}
              <span className={cn(
                "font-medium transition-all duration-300 ease-out relative z-10",
                isActive 
                  ? "text-primary font-semibold" 
                  : "group-hover:font-medium"
              )}>
                {t(item.labelKey)}
              </span>
              
            </button>
          );
        })}
      </div>
    </nav>
  );
}
